import React from 'react'
import { Box, VStack, Text, Spinner } from '@chakra-ui/react'
import { FormProvider, useFormContext } from './context/FormContext'
import { DonationFormUiProvider } from './context/UiContext'
import { StepNavigator } from './StepNavigator'
import { AmountStep } from './steps/AmountStep'
import { GiftStep } from './steps/GiftStep'
import { UpgradePaymentStep } from './steps/UpgradePaymentStep'
import { InformationStep } from './steps/InformationStep'
import { ReceiptStep } from './steps/ReceiptStep'
import { ChallengeProgress } from '../ChallengeProgress'

type MonthlyUpgradeDonationFormIdentifier =
  | { formId: string; formSlug?: never }
  | { formId?: never; formSlug: string };

export type MonthlyUpgradeDonationFormProps = MonthlyUpgradeDonationFormIdentifier & {
  jwt: string;
  utmCampaign: string | null;
  /** GA4 donatePlacement for this form's donate/purchase events. */
  placement?: string;
};

const MonthlyUpgradeDonationFormContent: React.FC = () => {
  const { state } = useFormContext()
  const { currentStep, formDetails, isLoading, error } = state

  if (isLoading) {
    return (
      <VStack gap={4} py={8}>
        <Spinner size="xl" color="teal.solid" />
        <Text color="fg.muted">Loading donation form...</Text>
      </VStack>
    )
  }

  if (error) {
    return (
      <Box p={6} bg="red.subtle" borderRadius="md">
        <Text fontWeight="semibold" color="red.fg" mb={2}>
          Error Loading Form
        </Text>
        <Text color="red.fg">{error}</Text>
      </Box>
    )
  }

  if (!formDetails) {
    return null
  }

  const hasGifts = formDetails.gifts.length > 0

  const showChallengeBanner =
    Boolean(formDetails.matching_challenge) &&
    (currentStep === 'amount' || currentStep === 'gift' || currentStep === 'payment');

  return (
    <VStack align="stretch" overflow="visible">
      {currentStep !== 'receipt' && <StepNavigator />}

      {showChallengeBanner ? (
        <Box display="flex" justifyContent="center">
          <ChallengeProgress
            challenge={formDetails.matching_challenge!}
            showStars={true}
            showHeadline={true}
            primaryColor="river"
            secondaryColor="river.inverted"
            textColor="river.inverted"
          />
        </Box>
      ) : null}

      <Box overflow="visible">
        {currentStep === 'amount' && <AmountStep />}
        {currentStep === 'gift' && hasGifts && <GiftStep />}
        {currentStep === 'payment' && <UpgradePaymentStep />}
        {currentStep === 'information' && <InformationStep />}
        {currentStep === 'receipt' && <ReceiptStep />}
      </Box>
    </VStack>
  )
}

export const MonthlyUpgradeDonationForm: React.FC<MonthlyUpgradeDonationFormProps> = (props) => {
  const { jwt, utmCampaign, placement } = props;
  const formId = 'formId' in props ? props.formId : undefined;
  const formSlug = 'formSlug' in props ? props.formSlug : undefined;

  return (
    <Box containerType="inline-size" w="full" overflow="visible">
      <Box
        w="full"
        minW="280px"
        maxW="md"
        mx="auto"
        pt={{ base: 1, _cqMd: 2 }}
        px={{ base: 3, _cqMd: 6 }}
        pb={{ base: 3, _cqMd: 6 }}
        overflow="visible"
      >
        <DonationFormUiProvider showStepNavigator={true}>
          <FormProvider
            {...(formId ? { formId } : { formSlug: formSlug ?? '' })}
            mode="monthly-upgrade"
            upgradeJwt={jwt}
            utmCampaign={utmCampaign}
            placement={placement}
          >
            <MonthlyUpgradeDonationFormContent />
          </FormProvider>
        </DonationFormUiProvider>
      </Box>
    </Box>
  )
}


